import ruRU from '@/locales/ru-RU.json';
import enUS from '@/locales/en-US.json';
import frFR from '@/locales/fr-FR.json';
import { createI18n, type IntlNumberFormat, type NamedValue } from 'vue-i18n';

export interface I18TParams {
  key: string;
  params: NamedValue;
}

export enum Locale {
  RuRU = 'ru-RU',
  EnUS = 'en-US',
  FrFR = 'fr-FR',
}

export const SUPPORTED_LOCALES = [Locale.RuRU, Locale.EnUS, Locale.FrFR];

/** Expose shared.library as top-level library for @alphadoc packages hardcoded to library.* keys. */
function withLibraryAlias<T extends { shared?: { library?: unknown } }>(messages: T): T {
  return Object.assign({}, messages, { library: messages.shared?.library });
}

function ruRU_pluralRule(count: number) {
  if (count === 0) {
    return 2;
  }

  const teen = count > 10 && count < 20;
  const endsWithOne = count % 10 === 1;

  if (!teen && endsWithOne) {
    return 0;
  }

  if (!teen && count % 10 >= 2 && count % 10 <= 4) {
    return 1;
  }

  return 2;
}

function frFR_pluralRule(count: number) {
  return count > 1 ? 1 : 0;
}

const NUMBER_FORMAT: IntlNumberFormat = {
  decimal: {
    style: 'decimal',
    maximumFractionDigits: 2,
  },
} as const;

export const i18n = createI18n({
  legacy: false,
  locale: Locale.RuRU,
  pluralRules: {
    [Locale.RuRU]: ruRU_pluralRule,
    [Locale.FrFR]: frFR_pluralRule,
  },
  numberFormats: {
    [Locale.RuRU]: NUMBER_FORMAT,
    [Locale.EnUS]: NUMBER_FORMAT,
    [Locale.FrFR]: NUMBER_FORMAT,
  },
  messages: {
    // library.* — runtime alias for @alphadoc/alphadoc-gantt and @alphadoc/alphadoc-annotation
    [Locale.EnUS]: withLibraryAlias(enUS),
    [Locale.RuRU]: withLibraryAlias(ruRU),
    [Locale.FrFR]: withLibraryAlias(frFR),
  },
});

function ruRU_ordinalRule(count: number) {
  return count % 10 === 3 && count % 100 !== 13 ? 'ья' : 'ая';
}

function enUS_ordinalRule(count: number) {
  return count % 10 === 1 && count % 100 !== 11
    ? 'st'
    : count % 10 === 2 && count % 100 !== 12
      ? 'nd'
      : count % 10 === 3 && count % 100 !== 13
        ? 'rd'
        : 'th';
}

function frFR_ordinalRule(count: number) {
  return count === 1 ? 'er' : 'e';
}

const ORDINAL_RULES = {
  [Locale.RuRU]: ruRU_ordinalRule,
  [Locale.EnUS]: enUS_ordinalRule,
  [Locale.FrFR]: frFR_ordinalRule,
};

i18n.global.o = (key, count) => {
  return i18n.global.t(key, { count, suffix: ORDINAL_RULES[i18n.global.locale.value](count) });
};

const DATE_PLACEHOLDER_REGEXP = /^[._]+$/;
const I18N_KEY_REGEXP = /^\w+(?:\.\w+)+$/;

i18n.global.maybeT = (key, named) => {
  if (i18n.global.te(key)) {
    return named ? i18n.global.t(key, named) : i18n.global.t(key);
  }

  if (!DATE_PLACEHOLDER_REGEXP.test(key) && I18N_KEY_REGEXP.test(key)) {
    console.warn(`[i18n] "${key}" looks like a translation key but is missing`);
  }

  return key;
};

export default i18n;
